import { useApolloClient } from '@apollo/client';
import type { PageProps } from 'gatsby';
import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import PageLoader from '@components/PageLoader';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';
import { useSnackbar } from '@hooks/useSnackbar';

import { getQueryParams } from '@utils/url';

import { useCreateEmrConnection } from 'generated/emr.generated';

const messages = defineMessages({
  fail: {
    id: 'errorMessage.createEMRConnection',
    description: 'Failed to connect hospital/clinic. Please try again later.',
  },
});

/**
 * Page that handles emr connection auth response
 */
export default function EMRConnectionIntegratorResult({ params }: PageProps) {
  const client = useApolloClient();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const {
    state: { isAuthenticated },
  } = useSession();

  const [createEmrConnection] = useCreateEmrConnection();
  useEffect(() => {
    async function handleIntegratorResult() {
      if (params.result === 'success') {
        const { code, state } = getQueryParams();
        let result = '';

        if (typeof state === 'string') {
          result = Buffer.from(state, 'base64').toString();
        }
        if (result && code && typeof code === 'string') {
          const { fhir_api_endpoint_id, vitall_patient_id } =
            JSON.parse(result);

          await createEmrConnection({
            variables: {
              input: {
                vitall_patient_id,
                fhir_api_endpoint_id,
                authorization_code: code,
              },
            },
          });
        }
        // Delete all listDirectEmrApiConnections queries so that listDirectEmrApiConnections is refetched
        client.cache.evict({
          id: 'ROOT_QUERY',
          fieldName: 'listDirectEmrApiConnections',
        });
      } else {
        enqueueSnackbar({
          message: formatMessage(messages.fail),
          variant: 'error',
        });
      }
      navigate(ROUTES.HOSPITAL_CONNECTIONS);
    }
    if (isAuthenticated) {
      // Wait to be authenticated
      handleIntegratorResult();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return <PageLoader />;
}
